import { z } from 'zod';
import { LetterMix } from './awards.js';
import {
  LetterLibraryType,
  LetterSignerRole,
  LetterSignerSource,
  LetterTemplateView,
  LetterVariant,
  SignatureConfirmation,
} from './letters.js';

/**
 * APP-130 slice 1: Settings › Elections and letters › Letters. One coverage grid per award type:
 * rows are signers, columns are the cash and options mixes ordinary entry can produce (D3), and
 * each cell reads Ready (vN), Sample to review, Approved (Use it) or Missing. A cell is award type
 * × signer × mix (D1); its key (`SDB.<signerId>.cash_only`) is the `?cell=` deep link.
 */

const PersonRef = z.object({ id: z.uuid(), name: z.string() });

/** A signer as a row shows them: the title is the one on their person record. */
export const LetterSigner = PersonRef.extend({
  title: z.string().nullable(),
  /** Still active in the directory; a signer who left can't sign new letters. */
  active: z.boolean(),
});
export type LetterSigner = z.infer<typeof LetterSigner>;

/**
 * APP-212: `sample`, no real letter, so MyRewards' built-in sample letter is made in the cell.
 * APP-221: `not_needed`, a column that needs no letter, with none of its own (`notNeeded` says why).
 */
export const LetterCellStateName = z.enum([
  'ready',
  'sample_to_review',
  'approved',
  'sample',
  'missing',
  'not_needed',
]);
export type LetterCellStateName = z.infer<typeof LetterCellStateName>;

export const LetterCoverageCell = z.object({
  /** `letterCellKey`: `<type>.<signerId>.<mix>`. */
  key: z.string(),
  mix: LetterMix,
  state: LetterCellStateName,
  /**
   * Ready: the version letters use. Sample to review: the draft waiting. Approved: the approved
   * version not in use (Use it). Sample (the built-in sample letter), Missing and Not needed: null.
   */
  template: LetterTemplateView.nullable(),
  /** A newer draft waiting for review while the cell is Ready or Approved; approving it replaces. */
  waiting: LetterTemplateView.nullable(),
  /**
   * Missing or Sample after a Send back: the version sent back, so the cell offers a corrected
   * upload.
   */
  sentBack: LetterTemplateView.nullable(),
});
export type LetterCoverageCell = z.infer<typeof LetterCoverageCell>;

/** Why a row's signer can't be resolved: the codes generation itself fails with. */
export const LetterRowProblem = z.enum([
  'letter_policy_missing',
  'letter_signer_missing',
  'letter_signer_ambiguous',
]);
export type LetterRowProblem = z.infer<typeof LetterRowProblem>;

export const LetterCoverageRow = z.object({
  /** Who signs; null on a problem row. */
  signer: LetterSigner.nullable(),
  /** The awarding sector, on a Sector Lead row. */
  sector: z.object({ id: z.string(), name: z.string() }).nullable(),
  /** The signing policy the row comes from; null when the type has no single policy. */
  source: LetterSignerSource.nullable(),
  problem: LetterRowProblem.nullable(),
  /** One per shown mix, in `mixes` order; empty on a problem row. */
  cells: z.array(LetterCoverageCell),
});
export type LetterCoverageRow = z.infer<typeof LetterCoverageRow>;

/** Why a mix isn't a column (D3), for the line under the grid. */
export const LetterHiddenMix = z.object({
  mix: LetterMix,
  because: z.enum(['cash_only_type', 'paa_always_cash', 'paa_never_options', 'paa_never_cash']),
});
export type LetterHiddenMix = z.infer<typeof LetterHiddenMix>;

/**
 * APP-221: a column that needs no letter, and why, for the line under the grid. Its cells stay in
 * the grid and read Not needed unless a letter of their own is there.
 */
export const LetterNotNeededMix = z.object({
  mix: LetterMix,
  because: z.enum(['paa_always_options']),
});
export type LetterNotNeededMix = z.infer<typeof LetterNotNeededMix>;

export const LetterCoverageCounts = z.object({
  ready: z.number().int().nonnegative(),
  /** Drafts waiting for review, in Sample to review cells and beside a Ready or Approved one. */
  toReview: z.number().int().nonnegative(),
  /** Approved but not in use. */
  approved: z.number().int().nonnegative(),
  /** APP-212: cells where the built-in sample letter is made, for want of a real one. */
  sample: z.number().int().nonnegative(),
  /** No letter, or no signer: letters there can't be made today. Hidden mixes never count. */
  missing: z.number().int().nonnegative(),
  /** APP-221: cells shown that need no letter. */
  notNeeded: z.number().int().nonnegative(),
  /** The cells that need a letter: what Ready counts against. Not needed cells aren't in it. */
  total: z.number().int().nonnegative(),
});
export type LetterCoverageCounts = z.infer<typeof LetterCoverageCounts>;

export const LetterCoverageGrid = z.object({
  awardType: LetterLibraryType,
  mixes: z.array(LetterMix),
  hidden: z.array(LetterHiddenMix),
  notNeeded: z.array(LetterNotNeededMix),
  rows: z.array(LetterCoverageRow),
  counts: LetterCoverageCounts,
  /** Whether this person may upload, approve, use and send back this type's letters. */
  canEdit: z.boolean(),
});
export type LetterCoverageGrid = z.infer<typeof LetterCoverageGrid>;

export const LetterCoverage = z.object({
  /** The letter library's revision; every change to it moves this on. */
  revision: z.number().int().nonnegative(),
  /** The CEO / President who signs PAA, TAA and Spot letters (by default). */
  ceo: LetterSigner.nullable(),
  canEditCeo: z.boolean(),
  /** SDB, EDB, PAA and TAA, and Spot for those who keep Spot letters. */
  grids: z.array(LetterCoverageGrid),
  counts: LetterCoverageCounts,
});
export type LetterCoverage = z.infer<typeof LetterCoverage>;

const Reason = z.string().trim().min(1).max(500);

/** Approve and use for <mix>: approves the sample it names and maps the version into its cell. */
export const ApproveAndMapLetter = z
  .object({
    previewSha256: z.string().regex(/^[a-f0-9]{64}$/),
    reason: Reason,
    /** D98, SENT-031: the approver's tick, with the key of the signature warning they saw. */
    signatureConfirmed: SignatureConfirmation.optional(),
  })
  .strict();
export type ApproveAndMapLetter = z.infer<typeof ApproveAndMapLetter>;

export const ApproveAndMapResult = z.object({
  template: LetterTemplateView,
  /**
   * D6: the version this one replaces in its cell. It stays approved; `stillMapped` when it's a
   * general-purpose version that still serves the signer's other mixes.
   */
  replaced: z
    .object({
      id: z.uuid(),
      designVersion: z.number().int().positive(),
      name: z.string(),
      stillMapped: z.boolean(),
    })
    .nullable(),
});
export type ApproveAndMapResult = z.infer<typeof ApproveAndMapResult>;

/** Send back for a fix (D5). */
export const SendBackLetter = z.object({ reason: Reason }).strict();
export type SendBackLetter = z.infer<typeof SendBackLetter>;

/** Changes the CEO / President line only; signer policies and mapped versions stay. */
export const SetLetterCeo = z.object({ personId: z.uuid(), reason: Reason }).strict();
export type SetLetterCeo = z.infer<typeof SetLetterCeo>;

/**
 * APP-130 D97: a new name for a version that isn't retired. The name is the library's own label:
 * it isn't printed in the letter, and approval pins the sample's hash, not the name. The same
 * rule as the upload's name.
 */
export const RenameLetterTemplate = z
  .object({ name: z.string().trim().min(1).max(120), reason: Reason })
  .strict();
export type RenameLetterTemplate = z.infer<typeof RenameLetterTemplate>;

/**
 * APP-130 D97: a draft's new signer, the role they sign as, and/or its cash and options mix. Only
 * a draft can move (not approved, sent back or retired): an approved letter changes by a new
 * version, so its approval stays tied to the letter that was checked. What's left out stays.
 */
export const ChangeLetterDesign = z
  .object({
    signerId: z.uuid().optional(),
    signerRole: LetterSignerRole.optional(),
    variant: LetterVariant.optional(),
    reason: Reason,
  })
  .strict()
  .refine(
    (v) => v.signerId !== undefined || v.signerRole !== undefined || v.variant !== undefined,
    { message: 'Choose a new signer or mix.', path: ['signerId'] },
  );
export type ChangeLetterDesign = z.infer<typeof ChangeLetterDesign>;
